import React, {useCallback, useEffect, useState } from 'react';
import { Input, Select, Tooltip } from 'antd';
import { ChevronDown, Info, ChevronUp } from '../../assets/icons';
import { Images } from '../../config/images';
import './PropertyPerformance.scss';
import { TOOLTIP_TEXT } from '../../utils/constant';
import { useSelector } from 'react-redux';
import Chart from '../Charts/HypotheticalReturnChart';
import { debounce } from '../../utils';
import { getUsCurrencyFormat } from '../../utils/usCurrencyFormatter';
import { LazyLoadImage } from 'react-lazy-load-image-component';

const PropertyPerformance = (props) => {
  const [arrowUp, setArrowUp] = useState(false);
  const {
    data: {
      data: {
        state = '',
        city = '',
        finance: { highAppreciation , highDividend , avgAppreciation , avgDividend, lowAppreciation , lowDividend  } = {},
      } = {},
    } = {},
  } = useSelector((state) => state.propertyDetails);
  const [principal, setPrincipal] = useState('');
  const [hypotheticalReturnData, setHypotheticalReturnData] = useState([]);
  const [selectedHistoricalPerformance, setSelectedHistoricalPerformance] = useState('avg');
  const [options, setOptions] = useState ([
    {
      value: 'high',
      label: `High (${Number(Number(highAppreciation).toFixed(1)) || 6}% share appreciation, ${
        Number(Number(highDividend).toFixed(1)) || 4
      }% Dividends)`,
      sa: Number(Number(highAppreciation)?.toFixed(1)) || 6,
        dividend: Number(Number(highDividend)?.toFixed(1)) || 4,
    },
    {
      value: 'avg',
      label: `Average (${Number(Number(avgAppreciation)?.toFixed(1)) || 4}% share appreciation, ${
        Number(Number(avgDividend)?.toFixed(1)) || 2
      }% Dividends)`,
      sa: Number(Number(avgAppreciation)?.toFixed(1)) || 4,
        dividend: Number(Number(avgDividend)?.toFixed(1)) || 2,
    },
    {
      value: 'low',
      label: `Low (${Number(Number(lowAppreciation)?.toFixed(1)) || 2}% share appreciation, ${
        Number(Number(lowDividend)?.toFixed(1)) || 1
      }% Dividends)`,
      sa: Number(Number(lowAppreciation)?.toFixed(1)) || 2,
      dividend: Number(Number(lowDividend)?.toFixed(1)) || 1,
    },
  ])
  useEffect(()=>{  
    if (highAppreciation || highDividend || avgAppreciation || avgDividend || lowAppreciation || lowDividend) {
      let option1 = [
        {
          value: 'high',
          label: `High (${Number(Number(highAppreciation).toFixed(1)) || 6}% share appreciation, ${
            Number(Number(highDividend).toFixed(1)) || 4
          }% Dividends)`,
          sa: Number(Number(highAppreciation)?.toFixed(1)) || 6,
          dividend: Number(Number(highDividend)?.toFixed(1)) || 4,
        },
        {
          value: 'avg',
          label: `Average (${Number(Number(avgAppreciation)?.toFixed(1)) || 4}% share appreciation, ${
            Number(Number(avgDividend)?.toFixed(1)) || 2
          }% Dividends)`,
          sa: Number(Number(avgAppreciation)?.toFixed(1)) || 4,
          dividend: Number(Number(avgDividend)?.toFixed(1)) || 2,
        },
        {
          value: 'low',
          label: `Low (${Number(Number(lowAppreciation)?.toFixed(1)) || 2}% share appreciation, ${
            Number(Number(lowDividend)?.toFixed(1)) || 1
          }% Dividends)`,
          sa: Number(Number(lowAppreciation)?.toFixed(1)) || 2,
          dividend: Number(Number(lowDividend)?.toFixed(1)) || 1,
        },
      ] 
      setOptions( ()=> [...option1])
    }    
  } ,[highAppreciation , highDividend , avgAppreciation , avgDividend, lowAppreciation , lowDividend])

  const getHypotheticalReturnData = (principal, { sa, dividend }) => {
    const arr = [];

    if (principal) {
      for (let i = 0; i < 20; i++) {
        let obj = {
          year: '',
          currentYear: new Date().getFullYear() + i + 1,
          'Share appreciation': 0,
          Dividends: 0,
          'Market’s average historical growth': 0,
          saP: sa,
          dividendP: dividend,
        };
        obj.year = '';
        // Fractional interest appreciation calculated as compound interest formula 
        obj['Share appreciation'] = Number(principal) * Math.pow(1 + (Number(sa) / 100), i+1) - Number(principal); 
        obj['Market’s average historical growth'] = Number(principal) * Math.pow(1 + Number(sa) / 100, i+1) - Number(principal);
        if (i === 0) {
          obj.year = 'Today';
          obj.Dividends = ((Number(dividend) * Number(principal)) / 100) * (i+1); // Dividend calculated as simple interest formula
        } else {
          // let newPrincipal = Number(principal) + arr[i - 1]['Share appreciation'];
          // obj.Dividends = (Number(dividend) * newPrincipal) / 100;
          obj.Dividends = ((Number(dividend) * Number(principal)) / 100) * (i+1);
          // let newAvgPrincipal = Number(principal) + arr[i - 1]['Market’s average historical growth'];
          // let newMarketGrowth = (Number(averageData.dividend) * newAvgPrincipal) / 100;
          obj['Market’s average historical growth'] += obj.Dividends;
        }
        if (i === 19) {
          obj.year = new Date().getFullYear() + 20;
        }
        arr.push(obj);
      }
    }
    return arr;
  };

  const onInvestmentChange = (e) => {
    setPrincipal(e.target.value);
    setHypotheticalReturnData(
      getHypotheticalReturnData(
        e.target.value,
        options.find((x) => x.value === selectedHistoricalPerformance)
      )
    );
  };
  const onHistoricalPerformanceChange = (e) => {
    setSelectedHistoricalPerformance(e);
    setHypotheticalReturnData(
      getHypotheticalReturnData(
        principal,
        options.find((x) => x.value === e)
      )
    );
  };
  const onInvestedAmountChange = useCallback(debounce(onInvestmentChange), [principal, options,selectedHistoricalPerformance]);
  const onRiskChange = useCallback(debounce(onHistoricalPerformanceChange), [principal,options,selectedHistoricalPerformance]);

  return (
    <div className='py-64 property-performance'>
      <p className='mb-32 fz-24 fw-700 lh-13'>Hypothetical Investment Performance</p>
      <div className='return'>
        <div className='return__filters'>
          <div className='return__filters__item'>
            <p className='fw-600 mb-8'>Investment amount</p>
            <Input
              type='number'
              className='mb-24 amount-input'
              placeholder='Enter amount'
              onChange={onInvestedAmountChange}
              // Temporary comment CR changes.
              // onPaste={(e) => {
              //   e.preventDefault();
              //   const clipboardData = e.clipboardData || window.clipboardData;
              //   const pastedData = clipboardData.getData('text').slice(0, 10); // Limit pasted data to 10 characters
              //   const newValue = parseInt(pastedData);
              
              //   if (!isNaN(newValue) && newValue >= 0) {
              //     e.target.value = newValue;
              //     onInvestedAmountChange(e); // Trigger the onChange event with the new value
              //   }
              // }}
              onPaste={(e)=>{
                e.preventDefault();
              }}
              onKeyPress={(e) => {
                if (!/[0-9\b]/.test(e.key) || (e.target.value.length >= 10 && e.key !== 'Backspace' && e.key !== 'Delete')) {
                  e.preventDefault();
                }
              }}
              onWheel={(e) => {
                e.target.blur();
              }}
              controls={false}
            />
          </div>
          <div className='return__filters__item'>
            <p className='fw-600 mb-8'>
              Historical performance{' '}
              <Tooltip title={TOOLTIP_TEXT.HISTORICAL_PERFORMANCE(`${city} ${state}`)}>
                <span>
                  <Info marginLeft='8px' verticalAlign='sub' />
                </span>
              </Tooltip>
            </p>
            <div id='avg-anchor' />
            <Select
              placeholder
              defaultValue='avg'
              getPopupContainer={() => document.getElementById('avg-anchor')}
              onDropdownVisibleChange={() => setArrowUp(!arrowUp)}
              suffixIcon={arrowUp ? <ChevronUp color='#1A1519' /> : <ChevronDown color='#1A1519' />}
              className='return__filters__item--select'
              options={options}
              value={selectedHistoricalPerformance}
              onChange={onRiskChange}
            />
          </div>
        </div>
        <hr className='hr' />
        <div className='return__graph graph'>
          <p className='fw-600 fz-16'>Hypothetical return</p>
          {hypotheticalReturnData.length ? (
            <>
              <p className='fz-32 fw-700 text-taupe-80'>
                $
                {getUsCurrencyFormat(
                  (
                    hypotheticalReturnData[hypotheticalReturnData.length - 1]?.['Share appreciation'] +
                    hypotheticalReturnData[hypotheticalReturnData.length - 1]?.['Dividends'] +
                    Number(principal)
                  )?.toFixed(2)
                )}{' '}
                <span className='lh-13'>by {hypotheticalReturnData[hypotheticalReturnData.length - 1]?.['currentYear']}</span>
              </p>
              <Chart data={hypotheticalReturnData} selectedRisk={selectedHistoricalPerformance} />
            </>
          ) : (
            <LazyLoadImage src={Images.investmentAmount} alt='' />
          )}
          {!hypotheticalReturnData.length && (
            <div className='low-opacity'>
              <div className='graph__legend'>
                <div className='graph__legend__item'>
                  <div className='indicator share' />
                  <span className='label'>Share appreciation</span>
                </div>
                <div className='graph__legend__item'>
                  <div className='indicator dividend' />
                  <span className='label'>Dividends</span>
                </div>
                <div className='graph__legend__item'>
                  <div className='indicator growth' />
                  <span className='label'>Market’s avg. historical growth</span>
                </div>
              </div>
            </div>
          )}

          <p className='fz-12 mt-32 mb-0 text-taupe-60'>
            These hypothetical projections do not guarantee future returns.
          </p>
        </div>
      </div>
      {/* {propertyTypeName === PROPERTY_SUB_TYPE.VACATION ? (
        <>
          <p className='fw-600 fz-18 mt-40 mb-4'>Rental data</p>
          <p className='fz-12 mb-24 text-taupe-80'>
            Data provided by{' '}
            <a href='https://www.airdna.co/' className='text-terracotta underline'>
              AirDNA
            </a>
          </p>
          <p className='mb-24'>The performance shown below is for similar sized homes in the 75th percentile for this market.</p>
          <div className='chart-block'>
            <div className='chart-block__header'>
              <p className='title'>Daily rate</p>
              <div className='info'>
                <span className='text-taupe-80 mr-8'>Average range:</span>
                <span>$423 - $677</span>
              </div>
            </div>
            <img src={Images.graphBlock} alt='' />
          </div>
          <div className='chart-block'>
            <div className='chart-block__header'>
              <p className='title'>Monthly revenue</p>
              <div className='info'>
                <span className='text-taupe-80 mr-8'>Average range:</span>
                <span>$12,159 - $23,541</span>
              </div>
            </div>
            <img src={Images.graphBlock} alt='' />
          </div>
          <div className='chart-block'>
            <div className='chart-block__header'>
              <p className='title'>Monthly occupancy</p>
              <div className='info'>
                <span className='text-taupe-80 mr-8'>Average range:</span>
                <span>71% - 100%</span>
              </div>
            </div>
            <img src={Images.graphBlock} alt='' />
          </div>
        </>
      ) : (
        ''
      )} */}
    </div>
  );
};

export default PropertyPerformance;
